import { t as uiText } from '../i18n';
// src/components/Header.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../lib/firebase';
import NewWorksModal from './NewWorksModal';
import MemberListModal from './MemberListModal';
import WorkspaceSettingsModal from './WorkspaceSettingsModal';
import NotificationSettingsModal from './NotificationSettingsModal';
import UpgradeModal from './UpgradeModal';
import { useWorkspaceContext } from '../context/WorkspaceContext';
import { useSidebarContext } from '../context/SidebarContext';
import { useHashtagContext } from '../context/HashtagContext';
import { useSearchContext } from '../context/SearchContext';
import { useBoardListContext } from '../context/BoardListContext';
import { useWorkspaceMembersQuery } from '../features/auth/queries/workspaceMembersQueries';
import UserMenu from './UserMenu';
import { LanguageSwitcher } from './LanguageSwitcher';
import { refreshStorageURL, isStorageURL } from '../features/workspace/imageUtils';
import { getWorkspaceIconURL } from '../features/workspace/workspaceIconUtils';
import { HeaderIconButton } from './HeaderIconButton';
import FeedbackButton from './feedback/FeedbackButton';
import './Header.css';

interface HeaderProps {
  isEditMode?: boolean; // 編集モードフラグを追加
  isTitleEditing?: boolean; // タイトル編集中フラグを追加
  // onUndo?: () => void; // Undo機能のコールバック - 一時的に無効化（後日実装予定）
  // onRedo?: () => void; // Redo機能のコールバック - 一時的に無効化（後日実装予定）
  onInsertImage?: () => void; // 画像挿入機能のコールバック
  onBeforeHomeNavigation?: () => Promise<void>; // ホーム遷移前の保存処理
  onUserSettingsOpen?: () => void; // ユーザー設定を開くコールバック
  onHomeClick?: () => void; // ワークスペースホームクリック時の追加処理
  isSidebarToggleDisabled?: boolean; // サイドバートグルを無効化
  showSearchInput?: boolean; // ヘッダー中央の検索入力を表示するか
}

const Header: React.FC<HeaderProps> = ({
  isEditMode = false,
  isTitleEditing = false,
  onInsertImage,
  onBeforeHomeNavigation,
  onUserSettingsOpen,
  onHomeClick,
  isSidebarToggleDisabled = false,
  showSearchInput = false,
}) => {
  const [user] = useAuthState(auth as any);
  const [modalOpen, setModalOpen] = useState(false);
  const [workspaceDropdownOpen, setWorkspaceDropdownOpen] = useState(false);
  const [settingsMenuOpen, setSettingsMenuOpen] = useState(false);
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [workspaceSettingsModalOpen, setWorkspaceSettingsModalOpen] = useState(false);
  const [notificationSettingsModalOpen, setNotificationSettingsModalOpen] = useState(false);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [workspaceIconUrl, setWorkspaceIconUrl] = useState<string | null>(null);
  const [workspaceIconUrls, setWorkspaceIconUrls] = useState<Map<string, string | null>>(new Map());

  const { workspaceId: routeWorkspaceId } = useParams();

  // アニメーション状態管理
  const [searchAnimationState, setSearchAnimationState] = useState<'fade-in' | 'fade-out'>('fade-in');

  const workspaceDropdownRef = useRef<HTMLDivElement | null>(null);
  const settingsMenuRef = useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const {
    workspaces,
    refresh: refreshWorkspaces,
  } = useWorkspaceContext();

  const displayWorkspaceId = routeWorkspaceId;
  const displayWorkspace =
    displayWorkspaceId ? workspaces.find((w) => w.id === displayWorkspaceId) || null : null;
  const { data: membersMap } = useWorkspaceMembersQuery(displayWorkspaceId ?? '', !!displayWorkspaceId);

  const { toggleSidebarForWorkspace, isMobile, isOpen, closeSidebar } = useSidebarContext();
  const { selectedHashtag, setSelectedHashtag, refreshHashtags } = useHashtagContext();
  const { searchQuery, setSearchQuery, isSearching, setIsSearching } = useSearchContext();
  const { refreshBoardList } = useBoardListContext();
  const currentUserRole = React.useMemo(() => {
    if (!user || !membersMap) return 'member' as const;
    const memberInfo = membersMap.get(user.uid);
    return memberInfo?.role || 'member';
  }, [user, membersMap]);
  const isAdminOrOwner = currentUserRole === 'owner' || currentUserRole === 'admin';

  const shouldShowSearchBar = showSearchInput;

  // ワークスペース未参加（一覧に存在しない/未選択）時は、ヘッダー左のワークスペース表示をクリック不可にする
  const canNavigateWorkspaceHome =
    !!displayWorkspaceId && workspaces.some((w) => w.id === displayWorkspaceId);

  const handleWorkspaceHomeClick = async () => {
    // 念のため（通常はonClick自体を外す）
    if (!canNavigateWorkspaceHome) return;

    setWorkspaceDropdownOpen(false);
    setSettingsMenuOpen(false);
    setSelectedHashtag(null);

    setSearchQuery('');
    setIsSearching(false);

    refreshWorkspaces(); // ワークスペース一覧をリフレッシュ
    refreshHashtags(); // ハッシュタグ一覧をリフレッシュ

    // ボード一覧のリフレッシュを少し遅延させて確実に実行
    setTimeout(() => {
      refreshBoardList(); // ボード一覧をリフレッシュ
    }, 50); // 遅延時間を短縮してより早く実行

    // スマホでサイドバーが開いている場合は閉じる
    handleNonHamburgerClick();

    try {
      // エディターページの場合は保存処理を実行
      if (onBeforeHomeNavigation) {
        await onBeforeHomeNavigation();
      }
    } catch (error) {
      console.warn('⚠️ ホーム遷移前の保存処理でエラー:', error);
      // 保存エラーでも遷移は継続
    }

    if (displayWorkspaceId) {
      navigate(`/${displayWorkspaceId}`, { replace: true });
    }

    onHomeClick?.();
  };

  const handleSelectWorkspace = (id: string) => {
    setWorkspaceDropdownOpen(false);
    setSettingsMenuOpen(false);

    setSelectedHashtag(null);
    setSearchQuery('');
    setIsSearching(false);

    navigate(`/${id}`);
    handleNonHamburgerClick();
  };

  // アニメーション状態の管理
  useEffect(() => {
    if (shouldShowSearchBar) {
      setSearchAnimationState('fade-in');
    } else {
      setSearchAnimationState('fade-out');
    }
  }, [shouldShowSearchBar]);

  // ワークスペースアイコンの読み込み
  useEffect(() => {
    let isCancelled = false;

    const loadWorkspaceIcon = async () => {
      // 即座にアイコンをクリア（前のアイコンが表示されないように）
      setWorkspaceIconUrl(null);

      if (displayWorkspaceId) {
        try {
          const iconUrl = await getWorkspaceIconURL(displayWorkspaceId);

          // キャンセルされていない場合のみ状態を更新
          if (!isCancelled) {
            setWorkspaceIconUrl(iconUrl);
          } else {}
        } catch (error) {
          if (!isCancelled) {
            console.error('❌ ワークスペースアイコン読み込みエラー:', {
              workspaceId: displayWorkspaceId,
              error,
              timestamp: new Date().toISOString()
            });
            setWorkspaceIconUrl(null);
          }
        }
      } else {
        if (!isCancelled) {
          setWorkspaceIconUrl(null);
        }
      }
    };

    loadWorkspaceIcon();

    // クリーンアップ関数：コンポーネントのアンマウント時や依存関係の変更時にキャンセル
    return () => {
      isCancelled = true;
    };
  }, [displayWorkspaceId, displayWorkspace?.iconPath]);

  useEffect(() => {
    const loadWorkspaceIcons = async () => {
      const iconUrlPromises = workspaces.map(async (workspace) => {
        try {
          const iconUrl = await getWorkspaceIconURL(workspace.id);
          return { workspaceId: workspace.id, iconUrl };
        } catch (error) {
          console.error(`❌ [Header] ワークスペース ${workspace.id} アイコン読み込みエラー:`, error);
          return { workspaceId: workspace.id, iconUrl: null };
        }
      });

      const results = await Promise.all(iconUrlPromises);
      const newIconUrls = new Map<string, string | null>();
      results.forEach(({ workspaceId, iconUrl }) => {
        newIconUrls.set(workspaceId, iconUrl);
      });
      setWorkspaceIconUrls(newIconUrls);
    };

    if (workspaces.length > 0) {
      loadWorkspaceIcons();
    } else {
      setWorkspaceIconUrls(new Map());
    }
  }, [workspaces]);

  // スマホでサイドバーが開いている時に他の要素をタップした場合の処理
  const handleNonHamburgerClick = () => {
    if (isMobile && isOpen) {
      closeSidebar();
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (workspaceDropdownRef.current && !workspaceDropdownRef.current.contains(e.target as Node)) {
        setWorkspaceDropdownOpen(false);
      }
      if (settingsMenuRef.current && !settingsMenuRef.current.contains(e.target as Node)) {
        setSettingsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setWorkspaceDropdownOpen(false);
      setSettingsMenuOpen(false);
      setModalOpen(false);
      setMemberModalOpen(false);
      setWorkspaceSettingsModalOpen(false);
      setNotificationSettingsModalOpen(false);
      setUpgradeModalOpen(false);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <header
        style={{
          height: '50px',
          backgroundColor: 'var(--workspace-primary, #cfae91)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 3000,
        }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }}
        >
          {/* サイドバー開閉ボタン */}
          <button
            type="button"
            onClick={isSidebarToggleDisabled ? undefined : () => toggleSidebarForWorkspace(displayWorkspaceId)}
            disabled={isSidebarToggleDisabled}
            aria-disabled={isSidebarToggleDisabled}
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '4px',
              border: '1px solid white',
              backgroundColor: 'transparent',
              color: 'white',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: isSidebarToggleDisabled ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isSidebarToggleDisabled ? 0.4 : 1,
            }}
            title={isSidebarToggleDisabled ? uiText("The sidebar cannot be opened on the dashboard") : uiText("Toggle the sidebar")}
          >
            ☰
          </button>

          <div
            style={{ display: 'flex', alignItems: 'center', gap: '8px', position: 'relative' }}
          >
            {/* ワークスペースアイコン & ワークスペース名 */}
            <div
              onClick={canNavigateWorkspaceHome ? handleWorkspaceHomeClick : undefined}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 8px 4px 4px',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                cursor: canNavigateWorkspaceHome ? 'pointer' : 'default',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                if (!canNavigateWorkspaceHome) return;
                e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              }}
              onMouseLeave={(e) => {
                if (!canNavigateWorkspaceHome) return;
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title={
                canNavigateWorkspaceHome
                  ? uiText("{{v0}} - Click to return to home", { v0: displayWorkspace?.name || uiText("Workspace") })
                  : `${displayWorkspace?.name || uiText("Not in a workspace")}`
              }
            >
              {/* ワークスペースアイコン */}
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '4px',
                  backgroundColor: 'transparent',
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                  flexShrink: 0
                }}
              >
                {workspaceIconUrl ? (
                  <img
                    src={workspaceIconUrl}
                    alt={displayWorkspace?.name || uiText("Workspace")}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onLoad={(e) => {}}
                    onError={async (e) => {
                      const img = e.currentTarget;
                      const originalSrc = img.src;

                      // Firebase Storage URLの場合はリフレッシュを試行
                      if (isStorageURL(originalSrc)) {
                        try {
                          const refreshedURL = await refreshStorageURL(originalSrc);
                          if (refreshedURL && refreshedURL !== originalSrc) {
                            img.src = refreshedURL;
                            return; // リフレッシュ成功時は処理終了
                          }
                        } catch (error) {
                          console.warn('🖼️ [Header] ワークスペースアイコンURLリフレッシュ失敗:', {
                            workspaceId: displayWorkspaceId,
                            error,
                            timestamp: new Date().toISOString()
                          });
                        }
                      }

                      // リフレッシュ失敗またはStorage URLでない場合はフォールバック
                      img.style.display = 'none';
                      const textElement = img.nextElementSibling as HTMLElement;
                      if (textElement) {
                        textElement.style.display = 'flex';
                      }
                    }}
                  />
                ) : null}
                <div
                  style={{
                    width: '100%',
                    height: '100%',
                    display: workspaceIconUrl ? 'none' : 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: 'white'
                  }}
                >
                  {displayWorkspace?.name?.charAt(0) || 'A'}
                </div>
              </div>

              {/* ワークスペース名 */}
              <div
                style={{
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: '200px'
                }}
              >
                {displayWorkspace?.name || uiText("Workspace")}
              </div>
            </div>

            <div style={{ position: 'relative' }} ref={workspaceDropdownRef}>
              <button
                type="button"
                onClick={() => {
                  setWorkspaceDropdownOpen((prev) => !prev);
                  setSettingsMenuOpen(false);
                  handleNonHamburgerClick();
                }}
                aria-label={uiText("Switch workspace")}
                title={uiText("Switch workspace")}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: workspaceDropdownOpen ? 'rgba(255, 255, 255, 0.18)' : 'transparent',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!workspaceDropdownOpen) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!workspaceDropdownOpen) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M7 10l5 5 5-5z" />
                </svg>
              </button>

              {workspaceDropdownOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    left: '0',
                    width: 'min(360px, calc(100vw - 32px))',
                    maxHeight: '70vh',
                    overflowY: 'auto',
                    backgroundColor: '#fff',
                    boxShadow: '0 10px 24px rgba(0, 0, 0, 0.18)',
                    borderRadius: '8px',
                    zIndex: 10020,
                    border: '1px solid #eee'
                  }}
                >
                  {(() => {
                    type WorkspaceTreeNode = (typeof workspaces)[number] & { children: WorkspaceTreeNode[] };
                    const workspaceMap = new Map<string, WorkspaceTreeNode>();
                    const rootWorkspaces: WorkspaceTreeNode[] = [];

                    workspaces.forEach((workspace) => {
                      workspaceMap.set(workspace.id, { ...workspace, children: [] });
                    });

                    workspaces.forEach((workspace) => {
                      const node = workspaceMap.get(workspace.id);
                      if (!node) return;

                      if (!workspace.parentWorkspaceId) {
                        rootWorkspaces.push(node);
                        return;
                      }

                      const parent = workspaceMap.get(workspace.parentWorkspaceId);
                      if (parent) {
                        parent.children.push(node);
                      } else {
                        rootWorkspaces.push(node);
                      }
                    });

                    const renderWorkspace = (workspace: WorkspaceTreeNode, level: number = 0): React.ReactNode => (
                      <React.Fragment key={workspace.id}>
                        <button
                          type="button"
                          onClick={() => handleSelectWorkspace(workspace.id)}
                          style={{
                            width: '100%',
                            border: 'none',
                            backgroundColor: workspace.id === displayWorkspaceId ? 'var(--workspace-bg-soft, #E9D8C9)' : '#fff',
                            cursor: 'pointer',
                            paddingTop: '12px',
                            paddingRight: '16px',
                            paddingBottom: '12px',
                            paddingLeft: '16px',
                            borderBottom: '1px solid #eee',
                            textAlign: 'left'
                          }}
                          onMouseEnter={(e) => {
                            if (workspace.id !== displayWorkspaceId) {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (workspace.id !== displayWorkspaceId) {
                              e.currentTarget.style.backgroundColor = '#fff';
                            }
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {level > 0 && (
                              <div
                                style={{
                                  width: '20px',
                                  height: '20px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  color: 'rgba(0, 0, 0, 0.4)',
                                  fontSize: '14px',
                                  flexShrink: 0
                                }}
                              >
                                └
                              </div>
                            )}
                            <div
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '4px',
                                backgroundColor: '#f8f9fa',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #e9ecef',
                                overflow: 'hidden',
                                flexShrink: 0
                              }}
                            >
                              {workspaceIconUrls.get(workspace.id) ? (
                                <img
                                  src={workspaceIconUrls.get(workspace.id)!}
                                  alt={workspace.name}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : (
                                <span
                                  style={{
                                    fontSize: '14px',
                                    color: '#6c757d',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  {workspace.name.charAt(0)}
                                </span>
                              )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontWeight: 'bold', fontSize: '14px', color: '#333' }}>
                                {workspace.name}
                              </div>
                              <div
                                style={{
                                  fontSize: '12px',
                                  color: '#666',
                                  wordBreak: 'break-all',
                                  lineHeight: '1.2'
                                }}
                              >
                                {workspace.id}
                              </div>
                            </div>
                          </div>
                        </button>
                        {workspace.children.map((child) => renderWorkspace(child, level + 1))}
                      </React.Fragment>
                    );

                    return rootWorkspaces.map((workspace) => renderWorkspace(workspace, 0));
                  })()}

                  <button
                    type="button"
                    onClick={() => {
                      setWorkspaceDropdownOpen(false);
                      setModalOpen(true);
                    }}
                    style={{
                      width: '100%',
                      border: 'none',
                      textAlign: 'left',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderTop: '2px solid #eee',
                      backgroundColor: '#f8f9fa',
                      color: '#333',
                      fontWeight: 'bold',
                      fontSize: '14px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#e3f2fd';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#f8f9fa';
                    }}
                  >
                    {uiText("➕ Add Workspace")}
                  </button>
                </div>
              )}
            </div>

            <div style={{ position: 'relative' }} ref={settingsMenuRef}>
              <button
                type="button"
                onClick={() => {
                  setSettingsMenuOpen((prev) => !prev);
                  setWorkspaceDropdownOpen(false);
                  handleNonHamburgerClick();
                }}
                aria-label={uiText("Workspace settings")}
                title={uiText("Workspace settings")}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '6px',
                  border: 'none',
                  backgroundColor: settingsMenuOpen ? 'rgba(255, 255, 255, 0.18)' : 'transparent',
                  color: 'white',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: 0,
                  transition: 'background-color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  if (!settingsMenuOpen) {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.12)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!settingsMenuOpen) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </button>

              {settingsMenuOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: 'calc(100% + 8px)',
                    right: '0',
                    backgroundColor: '#fff',
                    boxShadow: '0 10px 24px rgba(0, 0, 0, 0.18)',
                    borderRadius: '8px',
                    minWidth: '220px',
                    zIndex: 10020,
                    border: '1px solid #eee',
                    overflow: 'hidden'
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setMemberModalOpen(true);
                      setSettingsMenuOpen(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#333',
                      border: 'none',
                      backgroundColor: '#fff',
                      textAlign: 'left'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = '#fff';
                    }}
                  >
                    {uiText("Member list / invites")}
                  </button>
                  {isAdminOrOwner && (
                    <button
                      type="button"
                      onClick={() => {
                        setWorkspaceSettingsModalOpen(true);
                        setSettingsMenuOpen(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#333',
                        border: 'none',
                        backgroundColor: '#fff',
                        textAlign: 'left'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }}
                    >
                      {uiText("Setup")}
                    </button>
                  )}
                  {isAdminOrOwner && (
                    <button
                      type="button"
                      onClick={() => {
                        setNotificationSettingsModalOpen(true);
                        setSettingsMenuOpen(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#333',
                        border: 'none',
                        backgroundColor: '#fff',
                        textAlign: 'left'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }}
                    >
                      {uiText("Notifications")}
                    </button>
                  )}
                  {isAdminOrOwner && (
                    <button
                      type="button"
                      onClick={() => {
                        setUpgradeModalOpen(true);
                        setSettingsMenuOpen(false);
                      }}
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#333',
                        border: 'none',
                        backgroundColor: '#fff',
                        textAlign: 'left'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#fff';
                      }}
                    >
                      {uiText("Change plan")}
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        {/* 中央の検索窓（スマホで編集モード時は非表示） */}
        <div 
          className={`header-search-container ${searchAnimationState}`}
          style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            flex: 1,
            maxWidth: '400px',
            margin: '0 20px',
            position: 'relative'
          }}
        >
          {shouldShowSearchBar && (
            <input
              type="text"
              placeholder={selectedHashtag ? uiText("Search in #{{selectedHashtag}}...", { selectedHashtag: selectedHashtag }) : uiText("Search...")}
              value={searchQuery}
              onChange={(e) => {
                const query = e.target.value;
                setSearchQuery(query);
                setIsSearching(query.trim().length > 0);
              }}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: 'none',
                borderRadius: '20px',
                fontSize: '16px', // iOS Safariの自動ズームを防ぐため16px以上に設定
                boxSizing: 'border-box',
                backgroundColor: '#fff',
                outline: 'none',
                color: '#333',
                // スマホでの見た目を調整
                ...(isMobile && {
                  transform: 'scale(0.875)', // 16px * 0.875 = 14px相当の見た目
                  transformOrigin: 'left center',
                  width: '114%', // transformで縮小される分を補償
                })
              }}
              onFocus={(e) => {
                e.target.style.outline = '2px solid var(--workspace-primary, #cfae91)';
                // スマホでサイドバーが開いている場合は閉じる
                handleNonHamburgerClick();
              }}
              onBlur={(e) => {
                e.target.style.outline = 'none';
              }}
            />
          )}
        </div>
        {/* 右側のメニュー */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '10px'
        }}>
          <HeaderIconButton label={uiText("Help")}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.7"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <path d="M12 17h.01" />
            </svg>
          </HeaderIconButton>
          {user && <FeedbackButton onOpen={handleNonHamburgerClick} />}
          <LanguageSwitcher />
          {user && (
            <UserMenu
              onUserSettingsOpen={onUserSettingsOpen}
              onAvatarClick={handleNonHamburgerClick}
            />
          )}
        </div>
        {/* 新規作成モーダル */}
        {modalOpen && (
          <NewWorksModal onClose={() => {
            setModalOpen(false);
          }} />
        )}
        {memberModalOpen && (
          <MemberListModal onClose={() => setMemberModalOpen(false)} />
        )}
        {workspaceSettingsModalOpen && (
          <WorkspaceSettingsModal onClose={() => setWorkspaceSettingsModalOpen(false)} />
        )}
        {notificationSettingsModalOpen && (
          <NotificationSettingsModal onClose={() => setNotificationSettingsModalOpen(false)} />
        )}
        {upgradeModalOpen && (
          <UpgradeModal isOpen={upgradeModalOpen} onClose={() => setUpgradeModalOpen(false)} />
        )}
      </header>
    </>
  );
};

export default Header; 